import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { EDIT_MODE_OFF } from '../../modules/analysis/consts.ts';
import { getDeviceWidthFromWindowWidth } from '../nullComponents/DeviceWidthDriver/getDeviceWidthFromWindowWidth.ts';
import {
  ActiveTour,
  DeviceWidth,
  AIV_ARV_TABS,
  AivArvActiveTab,
  UIState,
} from '../types.ts';
import { resolveFeatureGridFieldConflicts } from './actions/resolveFeatureGridFieldConflicts.ts';
import { setOrder } from './orderSlice.ts';

const ANALYSIS_TOUR_LENGTH = 14;

export const tourLengths: Record<ActiveTour, number> = { analysisTour: ANALYSIS_TOUR_LENGTH };

const AIV_ARV_TAB_STORAGE_KEY_PREFIX = 'aivArvActiveTab';

const DEFAULT_AIV_ARV_TAB: AivArvActiveTab = 'all';

export const getAivArvTabStorageKey = (orderId: string): string => {
  return `${AIV_ARV_TAB_STORAGE_KEY_PREFIX}-${orderId}`;
};

const loadAivArvTabFromLocalStorage = (orderId: string): AivArvActiveTab => {
  if (!orderId) {
    return DEFAULT_AIV_ARV_TAB;
  }
  try {
    const storageKey = getAivArvTabStorageKey(orderId);
    const stored = window.localStorage.getItem(storageKey);
    if (stored && AIV_ARV_TABS.includes(stored as AivArvActiveTab)) {
      return stored as AivArvActiveTab;
    }
  } catch {
    // Ignore localStorage errors (e.g., in private browsing mode)
  }
  return DEFAULT_AIV_ARV_TAB;
};

export const initialUIState: UIState = {
  analysisTourIndex: -1,
  activeTour: null,
  activeDepreciateSeparatelyID: EDIT_MODE_OFF,
  deviceWidth: getDeviceWidthFromWindowWidth(),
  isEditPropertyDetailsModalOpen: false,
  objectiveEditMode: EDIT_MODE_OFF,
  featureGridExpandedPropertyId: undefined,
  featureGridConflictedFields: {},
  selectedPriorSalesPropertyId: 'subject',
  aivArvActiveTab: DEFAULT_AIV_ARV_TAB,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState: initialUIState,
  reducers: {
    setActiveTour(state, action: PayloadAction<{ activeTour: UIState['activeTour'] }>) {
      Object.assign(state, { activeTour: action.payload.activeTour });
    },
    incrementActiveTourIndex(state, _action: PayloadAction) {
      switch (state.activeTour) {
        case 'analysisTour': {
          if (state.analysisTourIndex < ANALYSIS_TOUR_LENGTH - 1) {
            Object.assign(state, { analysisTourIndex: state.analysisTourIndex + 1 });
          }
          break;
        }
        default:
          break;
      }
    },
    decrementActiveTourIndex(state, _action: PayloadAction) {
      switch (state.activeTour) {
        case 'analysisTour': {
          if (state.analysisTourIndex > 0) {
            Object.assign(state, { analysisTourIndex: state.analysisTourIndex - 1 });
          }
          break;
        }
        default:
          break;
      }
    },
    resetActiveTourIndex(state, _action: PayloadAction) {
      switch (state.activeTour) {
        case 'analysisTour':
          Object.assign(state, { analysisTourIndex: -1 });
          break;
        default:
          break;
      }
    },
    setActiveDepreciateSeparatelyID(state, action: PayloadAction<string>) {
      Object.assign(state, { activeDepreciateSeparatelyID: action.payload });
    },
    setIsEditPropertyDetailsModalOpen(state, action: PayloadAction<boolean>) {
      Object.assign(state, { isEditPropertyDetailsModalOpen: action.payload });
    },
    setObjectiveEditMode(state, action: PayloadAction<string>) {
      Object.assign(state, {
        objectiveEditMode:
          state.objectiveEditMode === action.payload ? EDIT_MODE_OFF : action.payload,
      });
    },
    setDeviceWidth_DANGER_INTERNAL(state, action: PayloadAction<{ deviceWidth: DeviceWidth }>) {
      Object.assign(state, { deviceWidth: action.payload.deviceWidth });
    },
    setFeatureGridExpandedPropertyId(state, action: PayloadAction<string | undefined>) {
      Object.assign(state, { featureGridExpandedPropertyId: action.payload });
    },
    setSelectedPriorSalesPropertyId(state, action: PayloadAction<string>) {
      Object.assign(state, { selectedPriorSalesPropertyId: action.payload });
    },
    setAivArvActiveTab(state, action: PayloadAction<UIState['aivArvActiveTab']>) {
      Object.assign(state, { aivArvActiveTab: action.payload });
    },
    addFeatureGridConflictedField(
      state,
      action: PayloadAction<UIState['featureGridConflictedFields']>,
    ) {
      const newFeatureGridConflictedFields = { ...state.featureGridConflictedFields };
      Object.entries(action.payload).forEach(([key, value]) => {
        if (!Object.keys(newFeatureGridConflictedFields).includes(key)) {
          newFeatureGridConflictedFields[key as keyof UIState['featureGridConflictedFields']] =
            value;
        }
      });

      Object.assign(state, {
        featureGridConflictedFields: newFeatureGridConflictedFields,
      });
    },
  },

  extraReducers: (builder) => {
    builder.addCase(resolveFeatureGridFieldConflicts, (state, action) => {
      Object.assign(state, {
        featureGridConflictedFields: Object.fromEntries(
          Object.entries(state.featureGridConflictedFields).filter(
            ([key, _]) => !Object.keys(action.payload).includes(key),
          ),
        ),
      });
    });
    builder.addCase(setOrder, (state, action) => {
      // Load the order-specific tab selection from localStorage when order changes
      const orderId = action.payload;
      const savedTab = loadAivArvTabFromLocalStorage(orderId);
      Object.assign(state, { aivArvActiveTab: savedTab });
    });
  },
});

export const {
  setActiveTour,
  incrementActiveTourIndex,
  decrementActiveTourIndex,
  resetActiveTourIndex,
  setActiveDepreciateSeparatelyID,
  setIsEditPropertyDetailsModalOpen,
  setObjectiveEditMode,
  setDeviceWidth_DANGER_INTERNAL,
  setFeatureGridExpandedPropertyId,
  setSelectedPriorSalesPropertyId,
  setAivArvActiveTab,
  addFeatureGridConflictedField,
} = uiSlice.actions;

export const uiReducer = uiSlice.reducer;
